import { createContext, forwardRef, useContext, useId, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  DrawerBreakpoint,
  DrawerButtonProps,
  DrawerCloseProps,
  DrawerContentProps,
  DrawerPosition,
  DrawerProps,
  DrawerSideProps,
} from './types';

const positionClasses: Record<DrawerPosition, string> = {
  start: '',
  end: 'drawer-end',
};

const openFromClasses: Record<DrawerBreakpoint, string> = {
  sm: 'sm:drawer-open',
  md: 'md:drawer-open',
  lg: 'lg:drawer-open',
  xl: 'xl:drawer-open',
  '2xl': '2xl:drawer-open',
};

const hiddenFromClasses: Record<DrawerBreakpoint, string> = {
  sm: 'sm:hidden',
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
  '2xl': '2xl:hidden',
};

type DrawerContextProps = {
  id: string;
  openFrom?: DrawerBreakpoint;
};

const DrawerContext = createContext<DrawerContextProps>({ id: '' });

const DrawerBase = forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      className,
      position = 'start',
      openFrom,
      open,
      defaultOpen = false,
      onOpenChange,
      components,
      children,
      ...props
    },
    ref,
  ) => {
    const toggleId = useId();

    const contextValue = useMemo(
      () => ({ id: toggleId, openFrom }),
      [toggleId, openFrom],
    );

    const classes = twMerge(
      'drawer',
      positionClasses[position],
      openFrom && openFromClasses[openFrom],
      className,
    );

    const checkedProps =
      open === undefined ? { defaultChecked: defaultOpen } : { checked: open };

    return (
      <DrawerContext.Provider value={contextValue}>
        <div ref={ref} className={classes} {...props}>
          <input
            id={toggleId}
            type="checkbox"
            className="drawer-toggle"
            onChange={event => onOpenChange?.(event.target.checked)}
            {...checkedProps}
            {...components?.toggle}
          />
          {children}
        </div>
      </DrawerContext.Provider>
    );
  },
);

const Content = forwardRef<HTMLDivElement, DrawerContentProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('drawer-content', className)} {...props}>
      {children}
    </div>
  ),
);

const Side = forwardRef<HTMLDivElement, DrawerSideProps>(
  ({ className, components, children, ...props }, ref) => {
    const { id } = useContext(DrawerContext);

    return (
      <div ref={ref} className={twMerge('drawer-side', className)} {...props}>
        <label
          htmlFor={id}
          aria-label="close sidebar"
          {...components?.overlay}
          className={twMerge('drawer-overlay', components?.overlay?.className)}
        />
        {children}
      </div>
    );
  },
);

const DrawerButton = forwardRef<HTMLLabelElement, DrawerButtonProps>(
  ({ className, children, ...props }, ref) => {
    const { id, openFrom } = useContext(DrawerContext);

    const classes = twMerge(
      'drawer-button cursor-pointer',
      openFrom && hiddenFromClasses[openFrom],
      className,
    );

    return (
      <label ref={ref} htmlFor={id} className={classes} {...props}>
        {children}
      </label>
    );
  },
);

const Close = forwardRef<HTMLLabelElement, DrawerCloseProps>(
  ({ className, children, ...props }, ref) => {
    const { id } = useContext(DrawerContext);

    const classes = twMerge('btn btn-circle btn-ghost', className);

    return (
      <label ref={ref} htmlFor={id} className={classes} {...props}>
        {children ?? '✕'}
      </label>
    );
  },
);

DrawerBase.displayName = 'Drawer';
Content.displayName = 'Drawer.Content';
Side.displayName = 'Drawer.Side';
DrawerButton.displayName = 'Drawer.Button';
Close.displayName = 'Drawer.Close';

export const Drawer = Object.assign(DrawerBase, {
  Content,
  Side,
  Button: DrawerButton,
  Close,
});
